import { msg } from '@lingui/core/macro';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { getAccessOptions, getVisibilityOptions } from 'modules/template-editor/domain/consts/form/template/options';
import { Values } from 'modules/template-editor/domain/interface/form/template';
import valueTypeValidator from 'modules/template-editor/domain/utils/form/template/value-type-validator';
import { updateTemplate } from 'modules/template-editor/store/templates/actions';
import { resetPressedState } from 'modules/template-editor/store/templates/templates';

import TemplateForm from 'modules/template-editor/ui/components/elements/forms/template-form';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';

const PRESET: TemplatePresetsNameList = 'edit';

function useEditModal() {
  const { t } = useLingui();
  const dispatch = useDispatch();
  const dispatchWithToast = useDispatchWithToast();
  const { activeTemplate, templates, isProcessed, isFinished, currentProcess } = useTemplates();
  const { info } = useOrganizer();
  const isAdmin = useIsAdmin();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    // `async` + промис запроса — Formik держит кнопку формы запертой до ответа (IMGM-1561).
    async (values: Values) => {
      if (!info) {
        throw new Error('failed to update template: info is not defined');
      }

      if (!activeTemplate) return;
      const { width, height, name, visibility, access } = valueTypeValidator({
        values,
      });
      const resolution = { width: +width, height: +height };
      return dispatchWithToast(
        updateTemplate({
          id: activeTemplate?.id,
          name,
          resolution,
          visibility,
          access,
          ownerId: info.id,
        }),
        { error: msg`Не удалось обновить шаблон` },
      );
    },
    [activeTemplate, dispatchWithToast, info],
  );

  useEffect(() => {
    if (!isFinished) {
      return;
    }
    if (currentProcess !== 'update template') return;
    close();
    dispatch(resetPressedState());
  }, [isFinished, close, dispatch, currentProcess]);

  const initialValue = useMemo(
    () => ({
      width: activeTemplate?.resolution.width?.toString() ?? '',
      height: activeTemplate?.resolution.height?.toString() ?? '',
      name: activeTemplate?.name ?? '',
      visibility: getVisibilityOptions().find((option) => option.value === activeTemplate?.visibility) ?? getVisibilityOptions()[0],
      access: getAccessOptions().find((option) => option.value === activeTemplate?.access) ?? getAccessOptions()[0],
    }),
    [activeTemplate],
  );

  const existingNames = useMemo(
    () => templates.map((category) => category.name.trim()).filter((name) => name !== activeTemplate?.name),
    [templates, activeTemplate],
  );

  return {
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(templatePresets[PRESET].header)}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin}
          isLoading={isProcessed}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useEditModal;
